import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchBooths, fetchPublicLayoutConfig, Booth } from '../lib/api';
import {
  STALLS_DATA,
  PILLARS_DATA,
  StallGeometry,
} from '../data/floorplanData';
import {
  Layers,
  RefreshCw,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  CheckCircle2,
  Lock,
  Clock,
  Filter,
} from 'lucide-react';

export interface FloorPlanProps {
  onSelectBooth: (booth: Booth) => void;
  selectedBooth?: Booth | null;
}

type CategoryFilter = 'all' | 'shell_9' | 'premium_12' | 'grand_18' | 'island_36' | 'trading_table' | 'meeting_room';

export const FloorPlan: React.FC<FloorPlanProps> = ({ onSelectBooth, selectedBooth }) => {
  const [scale, setScale] = useState(1);
  const [hoveredStall, setHoveredStall] = useState<{
    stall: StallGeometry;
    status: 'available' | 'on_hold' | 'booked';
  } | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<CategoryFilter>('all');

  // TanStack Query polling every 10 seconds to keep floor state live
  const { data: remoteBooths = [], isLoading, isRefetching, refetch } = useQuery<Booth[]>({
    queryKey: ['booths'],
    queryFn: () => fetchBooths(),
    refetchInterval: 10000,
  });

  // Query: Fetch dynamic public layout config from backend
  const { data: layoutConfig } = useQuery({
    queryKey: ['public-floorplan-layout'],
    queryFn: () => fetchPublicLayoutConfig(),
    staleTime: 30000,
  });

  const activeStalls = layoutConfig?.stalls || STALLS_DATA;

  // Map remote status by booth id
  const statusMap = useMemo(() => {
    const map = new Map<string, { status: 'available' | 'on_hold' | 'booked'; priceInr?: number }>();
    for (const b of remoteBooths) {
      map.set(b.id, {
        status: b.status as 'available' | 'on_hold' | 'booked',
        priceInr: b.priceInr,
      });
    }
    return map;
  }, [remoteBooths]);

  // Combined stalls with live status
  const mergedStalls = useMemo(() => {
    return activeStalls.map((stall: StallGeometry) => {
      const live = statusMap.get(stall.id);
      return {
        ...stall,
        status: live?.status || stall.initialStatus,
        priceInr: live?.priceInr || stall.priceInr,
      };
    });
  }, [activeStalls, statusMap]);

  // Inventory counters
  const availableCount = mergedStalls.filter((s) => s.status === 'available').length;
  const onHoldCount = mergedStalls.filter((s) => s.status === 'on_hold').length;
  const bookedCount = mergedStalls.filter((s) => s.status === 'booked').length;

  const handleStallClick = (stall: typeof mergedStalls[0]) => {
    if (stall.status === 'available') {
      const boothObj: Booth = {
        id: stall.id,
        dimensions: stall.dimensions,
        areaSqm: stall.areaSqm,
        priceInr: stall.priceInr,
        status: stall.status,
      };
      onSelectBooth(boothObj);
    }
  };

  const handleZoom = (delta: number) => {
    setScale((prev) => Math.min(Math.max(0.65, Number((prev + delta).toFixed(2))), 1.9));
  };

  const handleResetZoom = () => {
    setScale(1);
  };

  // Helper for stall styling based on category and status in executive light CAD mode
  const getStallVisuals = (stall: typeof mergedStalls[0]) => {
    const isSelected = selectedBooth?.id === stall.id;
    const isDimmed = selectedCategory !== 'all' && stall.category !== selectedCategory;

    if (stall.status === 'booked') {
      return {
        fill: '#E2E8F0',
        stroke: '#94A3B8',
        strokeWidth: 1.2,
        textColor: '#475569',
        cursor: 'cursor-not-allowed',
        opacity: isDimmed ? 0.35 : 0.85,
        fillPattern: 'url(#bookedHatch)',
      };
    }

    if (stall.status === 'on_hold') {
      return {
        fill: '#FFEDD5',
        stroke: '#EA580C',
        strokeWidth: 1.8,
        strokeDasharray: '4 2',
        textColor: '#9A3412',
        cursor: 'cursor-not-allowed',
        opacity: isDimmed ? 0.4 : 1,
        className: 'booth-locked-pulse',
      };
    }

    // Available stalls colored by category with crisp borders
    let fill = '#F0F9FF';
    let stroke = '#0284C7';
    let textColor = '#0369A1';

    switch (stall.category) {
      case 'premium_12':
        fill = '#FEF3C7';
        stroke = '#D97706';
        textColor = '#92400E';
        break;
      case 'grand_18':
        fill = '#FFF1F2';
        stroke = '#E11D48';
        textColor = '#BE123C';
        break;
      case 'island_36':
        fill = '#FDF4FF';
        stroke = '#C026D3';
        textColor = '#A21CAF';
        break;
      case 'trading_table':
        fill = '#ECFDF5';
        stroke = '#0A3D27';
        textColor = '#064E3B';
        break;
      case 'meeting_room':
        fill = '#FAF5FF';
        stroke = '#9333EA';
        textColor = '#7E22CE';
        break;
      case 'shell_9':
      default:
        fill = '#F0F9FF';
        stroke = '#0284C7';
        textColor = '#0369A1';
        break;
    }

    if (isSelected) {
      stroke = '#0A3D27';
      fill = '#D1FAE5';
      textColor = '#064E3B';
    }

    return {
      fill,
      stroke,
      strokeWidth: isSelected ? 2.5 : 1.4,
      textColor,
      cursor: 'cursor-pointer hover:opacity-100 transition-all duration-150',
      opacity: isDimmed ? 0.25 : 1,
    };
  };

  return (
    <div className="relative w-full rounded-3xl bg-white border border-slate-200/90 shadow-xl overflow-hidden flex flex-col">
      {/* Top Header & Live Counter Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4 px-6 py-4 bg-[#F8F9F6] border-b border-slate-200">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-2xl bg-emerald-50 border border-emerald-200 text-[#0A3D27]">
            <Layers className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-base font-bold text-slate-900 tracking-tight flex items-center gap-2">
              <span>Pavilion 3 Exhibition Layout</span>
              <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-[#0A3D27] border border-emerald-200">
                JWCC, Mumbai
              </span>
            </h2>
            <p className="text-xs text-slate-500">
              Interactive 2D Blueprint • Real-time inventory polling & atomic locking
            </p>
          </div>
        </div>

        {/* Live Counters & Refresh Button */}
        <div className="flex items-center gap-3 text-xs font-semibold">
          <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-xl border border-slate-200 shadow-2xs">
            <span className="w-2.5 h-2.5 rounded-full bg-[#0A3D27]" />
            <span className="text-slate-500">Available:</span>
            <span className="text-[#0A3D27] font-bold">{availableCount}</span>
          </div>

          <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-xl border border-slate-200 shadow-2xs">
            <span className="w-2.5 h-2.5 rounded-full bg-amber-500 animate-pulse" />
            <span className="text-slate-500">On Hold:</span>
            <span className="text-amber-800 font-bold">{onHoldCount}</span>
          </div>

          <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-xl border border-slate-200 shadow-2xs">
            <span className="w-2.5 h-2.5 rounded-full bg-slate-400" />
            <span className="text-slate-500">Booked:</span>
            <span className="text-slate-700 font-bold">{bookedCount}</span>
          </div>

          <button
            onClick={() => refetch()}
            title="Refresh floorplan state"
            className="p-2 rounded-xl text-slate-500 hover:text-slate-900 bg-white border border-slate-200 hover:border-slate-300 transition-colors cursor-pointer shadow-2xs"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isRefetching ? 'animate-spin text-[#0A3D27]' : ''}`} />
          </button>
        </div>
      </div>

      {/* Blueprint Legend Bar */}
      <div className="px-6 py-3 bg-white border-b border-slate-200 flex flex-wrap items-center justify-between gap-3 text-xs">
        <div className="flex flex-wrap items-center gap-4">
          <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
            <Filter className="w-3.5 h-3.5 text-[#0A3D27]" />
            Stall Specs:
          </span>

          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
              selectedCategory === 'all'
                ? 'bg-[#0A3D27] text-white shadow-xs'
                : 'text-slate-600 hover:text-slate-900 bg-slate-50'
            }`}
          >
            All Stalls ({mergedStalls.length})
          </button>

          <button
            onClick={() => setSelectedCategory('shell_9')}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg transition-all cursor-pointer ${
              selectedCategory === 'shell_9' ? 'bg-sky-100 text-sky-900 ring-1 ring-sky-400' : 'text-slate-600 hover:text-sky-800'
            }`}
          >
            <span className="w-3.5 h-3.5 rounded border border-sky-500 bg-sky-100" />
            <span>3 x 3 = 9 sqm</span>
          </button>

          <button
            onClick={() => setSelectedCategory('premium_12')}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg transition-all cursor-pointer ${
              selectedCategory === 'premium_12' ? 'bg-amber-100 text-amber-900 ring-1 ring-amber-400' : 'text-slate-600 hover:text-amber-800'
            }`}
          >
            <span className="w-3.5 h-3.5 rounded border border-amber-500 bg-amber-100" />
            <span>4 x 3 = 12 sqm</span>
          </button>

          <button
            onClick={() => setSelectedCategory('grand_18')}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg transition-all cursor-pointer ${
              selectedCategory === 'grand_18' ? 'bg-rose-100 text-rose-900 ring-1 ring-rose-400' : 'text-slate-600 hover:text-rose-800'
            }`}
          >
            <span className="w-3.5 h-3.5 rounded border border-rose-500 bg-rose-100" />
            <span>6 x 3 = 18 sqm</span>
          </button>

          <button
            onClick={() => setSelectedCategory('island_36')}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg transition-all cursor-pointer ${
              selectedCategory === 'island_36' ? 'bg-fuchsia-100 text-fuchsia-900 ring-1 ring-fuchsia-400' : 'text-slate-600 hover:text-fuchsia-800'
            }`}
          >
            <span className="w-3.5 h-3.5 rounded border border-fuchsia-500 bg-fuchsia-100" />
            <span>6 x 6 = 36 sqm</span>
          </button>

          <button
            onClick={() => setSelectedCategory('trading_table')}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg transition-all cursor-pointer ${
              selectedCategory === 'trading_table' ? 'bg-emerald-100 text-[#0A3D27] ring-1 ring-emerald-500' : 'text-slate-600 hover:text-[#0A3D27]'
            }`}
          >
            <span className="w-3.5 h-3.5 rounded border border-[#0A3D27] bg-emerald-100" />
            <span>6 sqm Trading Table</span>
          </button>

          <button
            onClick={() => setSelectedCategory('meeting_room')}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg transition-all cursor-pointer ${
              selectedCategory === 'meeting_room' ? 'bg-purple-100 text-purple-900 ring-1 ring-purple-400' : 'text-slate-600 hover:text-purple-800'
            }`}
          >
            <span className="w-3.5 h-3.5 rounded border border-purple-500 bg-purple-100" />
            <span>9 sqm Meeting Room</span>
          </button>
        </div>
      </div>

      {/* SVG Canvas Area (Executive Light CAD drafting surface) */}
      <div className="relative w-full overflow-auto p-2 sm:p-6 flex items-center justify-center min-h-[580px] bg-[#F1F5F9]">
        {isLoading && mergedStalls.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-24 text-slate-500">
            <div className="w-10 h-10 border-2 border-[#0A3D27] border-t-transparent rounded-full animate-spin" />
            <span className="text-sm font-medium">Loading Pavilion 3 Exhibition Floorplan...</span>
          </div>
        ) : (
          <div
            className="w-full max-w-4xl transition-transform duration-200 ease-out origin-top"
            style={{ transform: `scale(${scale})` }}
          >
            <svg
              viewBox="0 0 724 920"
              className="w-full h-auto select-none drop-shadow-md"
              xmlns="http://www.w3.org/2000/svg"
            >
              <defs>
                {/* Architectural Blueprint Grid Pattern in subtle forest green */}
                <pattern id="archGrid" width="9.67" height="9.67" patternUnits="userSpaceOnUse">
                  <path d="M 9.67 0 L 0 0 0 9.67" fill="none" stroke="rgba(10, 61, 39, 0.05)" strokeWidth="0.5" />
                </pattern>
                <pattern id="majorGrid" width="58" height="58" patternUnits="userSpaceOnUse">
                  <path d="M 58 0 L 0 0 0 58" fill="none" stroke="rgba(10, 61, 39, 0.10)" strokeWidth="0.8" />
                </pattern>

                {/* Booked Stalls Hatch Pattern */}
                <pattern id="bookedHatch" width="8" height="8" patternTransform="rotate(45 0 0)" patternUnits="userSpaceOnUse">
                  <line x1="0" y1="0" x2="0" y2="8" stroke="#94A3B8" strokeWidth="1.2" />
                </pattern>

                {/* Facility Glows / Light Fills */}
                <radialGradient id="cafeteriaGlow" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#FEF3C7" stopOpacity="0.8" />
                  <stop offset="100%" stopColor="#FDE68A" stopOpacity="0.4" />
                </radialGradient>

                <radialGradient id="confHallGlow" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#EDE9FE" stopOpacity="0.9" />
                  <stop offset="100%" stopColor="#DDD6FE" stopOpacity="0.5" />
                </radialGradient>

                {/* Pillar cross hatch pattern */}
                <pattern id="pillarHatch" width="4" height="4" patternTransform="rotate(45 0 0)" patternUnits="userSpaceOnUse">
                  <line x1="0" y1="0" x2="0" y2="4" stroke="#EF4444" strokeWidth="1" />
                </pattern>
              </defs>

              {/* Hall Outer Boundary & Floor Canvas */}
              <rect x="36" y="160" width="652" height="715" rx="8" fill="#FFFFFF" stroke="#0A3D27" strokeWidth="2" />
              <rect x="36" y="160" width="652" height="715" rx="8" fill="url(#archGrid)" />
              <rect x="36" y="160" width="652" height="715" rx="8" fill="url(#majorGrid)" />

              {/* 1. CAFETERIA FACILITY */}
              <g className="cafeteria-zone">
                <rect
                  x="374"
                  y="189"
                  width="299"
                  height="114"
                  rx="6"
                  fill="url(#cafeteriaGlow)"
                  stroke="#D97706"
                  strokeWidth="1.5"
                />
                {/* Food Counters along top wall */}
                <g fill="none" stroke="#D97706" strokeWidth="1">
                  <rect x="382" y="191" width="50" height="14" rx="2" fill="#FEF3C7" />
                  <text x="407" y="201" textAnchor="middle" fill="#92400E" fontSize="7" fontWeight="bold">COUNTER-1</text>

                  <rect x="438" y="191" width="50" height="14" rx="2" fill="#FEF3C7" />
                  <text x="463" y="201" textAnchor="middle" fill="#92400E" fontSize="7" fontWeight="bold">COUNTER-2</text>

                  <rect x="502" y="191" width="50" height="14" rx="2" fill="#FEF3C7" />
                  <text x="527" y="201" textAnchor="middle" fill="#92400E" fontSize="7" fontWeight="bold">COUNTER-3</text>

                  <rect x="558" y="191" width="50" height="14" rx="2" fill="#FEF3C7" />
                  <text x="583" y="201" textAnchor="middle" fill="#92400E" fontSize="7" fontWeight="bold">COUNTER-4</text>

                  <rect x="614" y="191" width="50" height="14" rx="2" fill="#FEF3C7" />
                  <text x="639" y="201" textAnchor="middle" fill="#92400E" fontSize="7" fontWeight="bold">COUNTER-5</text>
                </g>

                {/* Cafeteria Title */}
                <text x="523" y="250" textAnchor="middle" fill="#78350F" fontSize="16" fontWeight="900" letterSpacing="0.08em">
                  CAFETERIA
                </text>
                <text x="635" y="280" textAnchor="middle" fill="#92400E" fontSize="9" fontWeight="bold">
                  31m x 12m
                </text>
                <text x="635" y="293" textAnchor="middle" fill="#B45309" fontSize="11" fontWeight="extrabold">
                  472 sqm
                </text>

                {/* Entry arrows from walkway */}
                <path d="M 440 307 L 440 298 M 438 301 L 440 298 L 442 301" stroke="#D97706" strokeWidth="1.5" />
                <path d="M 448 298 L 448 307 M 446 304 L 448 307 L 450 304" stroke="#D97706" strokeWidth="1.5" />
              </g>

              {/* 2. CONFERENCE HALL FACILITY */}
              <g className="conference-zone">
                <rect
                  x="546"
                  y="308"
                  width="127"
                  height="215"
                  rx="6"
                  fill="url(#confHallGlow)"
                  stroke="#7C3AED"
                  strokeWidth="1.5"
                />

                {/* Seating Rows Illustration */}
                <text x="609" y="322" textAnchor="middle" fill="#5B21B6" fontSize="7" fontWeight="bold" letterSpacing="0.04em">
                  THEATER SEATING 112 PAX
                </text>
                {[0, 1, 2, 3, 4].map((r) => (
                  <g key={r} stroke="#8B5CF6" strokeWidth="0.8" fill="none" opacity="0.7">
                    <line x1="556" y1={330 + r * 8} x2="656" y2={330 + r * 8} strokeDasharray="3 2" />
                  </g>
                ))}

                <text x="609" y="380" textAnchor="middle" fill="#6D28D9" fontSize="7.5" fontWeight="bold">
                  TOTAL SEATING 128 PAX
                </text>

                {/* VIP Sofa Area */}
                <text x="609" y="445" textAnchor="middle" fill="#5B21B6" fontSize="7" fontWeight="bold">
                  SOFA SEATING 16 PAX
                </text>
                <text x="609" y="458" textAnchor="middle" fill="#4C1D95" fontSize="9" fontWeight="900" letterSpacing="0.05em">
                  CONFERENCE HALL
                </text>

                {/* Stage */}
                <rect x="560" y="468" width="100" height="42" rx="3" fill="#ECFDF5" stroke="#0A3D27" strokeWidth="1.2" />
                <text x="610" y="488" textAnchor="middle" fill="#0A3D27" fontSize="9" fontWeight="bold" letterSpacing="0.05em">
                  STAGE
                </text>
                <text x="645" y="505" textAnchor="middle" fill="#065F46" fontSize="7" fontWeight="medium">
                  22 X 16
                </text>

                {/* Conference Dimensions */}
                <text x="610" y="520" textAnchor="middle" fill="#6D28D9" fontSize="8" fontWeight="bold">
                  23m x 13m — 299 sqm
                </text>

                {/* Right AV Console */}
                <rect x="668" y="360" width="5" height="50" rx="1" fill="#E2E8F0" stroke="#94A3B8" strokeWidth="0.8" />
                <text x="670" y="388" textAnchor="middle" fill="#475569" fontSize="6" fontWeight="bold" transform="rotate(90 670 388)">
                  Console
                </text>

                {/* Entrance arrows */}
                <path d="M 538 318 L 546 318 M 543 315 L 546 318 L 543 321" stroke="#7C3AED" strokeWidth="1.2" />
                <path d="M 546 325 L 538 325 M 541 322 L 538 325 L 541 328" stroke="#7C3AED" strokeWidth="1.2" />
                <path d="M 538 410 L 546 410 M 543 407 L 546 410 L 543 413" stroke="#7C3AED" strokeWidth="1.2" />
              </g>

              {/* 3. ORGANIZER OFFICE */}
              <g className="organizer-office">
                <rect x="53" y="686" width="58" height="34" rx="4" fill="#FAF5FF" stroke="#9333EA" strokeWidth="1.5" />
                <text x="82" y="700" textAnchor="middle" fill="#581C87" fontSize="7.5" fontWeight="bold">
                  ORGANIZER
                </text>
                <text x="82" y="709" textAnchor="middle" fill="#581C87" fontSize="7" fontWeight="bold">
                  OFFICE
                </text>
                <text x="82" y="717" textAnchor="middle" fill="#7E22CE" fontSize="6.5">
                  6m x 3m = 18 sqm
                </text>
              </g>

              {/* 4. MAIN ENTRY / EXIT GATEWAY */}
              <g className="entry-exit-zone">
                {/* Entry Barrier Line */}
                <line x1="53" y1="682" x2="330" y2="682" stroke="#1D4ED8" strokeWidth="3" />
                <line x1="360" y1="682" x2="520" y2="682" stroke="#1D4ED8" strokeWidth="3" />
                <line x1="520" y1="682" x2="520" y2="760" stroke="#1D4ED8" strokeWidth="3" />

                {/* Gate Flow Arrows */}
                <path d="M 345 690 L 345 675 M 342 679 L 345 675 L 348 679" stroke="#0A3D27" strokeWidth="2" />
                <path d="M 355 675 L 355 690 M 352 686 L 355 690 L 358 686" stroke="#EF4444" strokeWidth="2" />

                {/* Turnstile Indicators */}
                <path d="M 185 750 L 185 725 M 181 730 L 185 725 L 189 730" stroke="#0A3D27" strokeWidth="2.5" />
                <path d="M 197 725 L 197 750 M 193 745 L 197 750 L 201 745" stroke="#EF4444" strokeWidth="2.5" />

                <text x="191" y="775" textAnchor="middle" fill="#0F172A" fontSize="13" fontWeight="900" letterSpacing="0.06em">
                  ENTRY / EXIT
                </text>
              </g>

              {/* 5. REGISTRATION & SECRETARIAT */}
              <g className="registration-zone">
                <rect x="335" y="745" width="115" height="30" rx="4" fill="#F5F3FF" stroke="#7C3AED" strokeWidth="1.5" />
                <text x="392" y="760" textAnchor="middle" fill="#4C1D95" fontSize="10" fontWeight="bold">
                  REGISTRATION
                </text>
                <text x="392" y="770" textAnchor="middle" fill="#6D28D9" fontSize="7">
                  12m x 3m = 36 sqm
                </text>

                {/* Secretariat Helpdesk */}
                <rect x="450" y="745" width="28" height="30" rx="3" fill="#EDE9FE" stroke="#6D28D9" strokeWidth="1" />
                <text x="464" y="758" textAnchor="middle" fill="#4C1D95" fontSize="5" fontWeight="bold">
                  SECRETARIAT
                </text>
                <text x="464" y="768" textAnchor="middle" fill="#6D28D9" fontSize="4.5">
                  Badge Help
                </text>
              </g>

              {/* 6. STRUCTURAL PILLARS */}
              {PILLARS_DATA.map((p, idx) => (
                <g key={`pillar-${idx}`}>
                  <circle cx={p.cx} cy={p.cy} r={p.r} fill="none" stroke="#DC2626" strokeWidth="1.5" strokeDasharray="2 1" />
                  <rect
                    x={p.cx - p.r * 0.7}
                    y={p.cy - p.r * 0.7}
                    width={p.r * 1.4}
                    height={p.r * 1.4}
                    fill="url(#pillarHatch)"
                    stroke="#DC2626"
                    strokeWidth="1"
                    transform={`rotate(45 ${p.cx} ${p.cy})`}
                  />
                </g>
              ))}

              {/* 7. ALL EXHIBITION STALLS */}
              {mergedStalls.map((stall) => {
                const visuals = getStallVisuals(stall);

                return (
                  <g
                    key={stall.id}
                    onClick={() => handleStallClick(stall)}
                    onMouseEnter={() =>
                      setHoveredStall({
                        stall,
                        status: stall.status === 'reserved' ? 'booked' : stall.status,
                      })
                    }
                    onMouseLeave={() => setHoveredStall(null)}
                    className="group"
                    opacity={visuals.opacity}
                  >
                    {/* Stall Bounding Box */}
                    <rect
                      x={stall.x}
                      y={stall.y}
                      width={stall.w}
                      height={stall.h}
                      rx="3"
                      fill={stall.status === 'booked' ? visuals.fillPattern : visuals.fill}
                      stroke={visuals.stroke}
                      strokeWidth={visuals.strokeWidth}
                      strokeDasharray={(visuals as any).strokeDasharray}
                      className={visuals.cursor}
                    />

                    {/* Stall ID Text */}
                    <text
                      x={stall.x + stall.w / 2}
                      y={stall.h >= 40 ? stall.y + stall.h / 2 - 6 : stall.y + stall.h / 2 - 2}
                      textAnchor="middle"
                      dominantBaseline="central"
                      fill={visuals.textColor}
                      fontSize={stall.areaSqm >= 36 ? '11' : stall.areaSqm >= 18 ? '9.5' : '7.5'}
                      fontWeight="bold"
                      className="pointer-events-none tracking-tight select-none"
                    >
                      {stall.id}
                    </text>

                    {/* Dimensions & Area Subtext */}
                    {stall.h >= 24 && (
                      <text
                        x={stall.x + stall.w / 2}
                        y={stall.h >= 40 ? stall.y + stall.h / 2 + 7 : stall.y + stall.h / 2 + 7}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fill={stall.status === 'booked' ? '#64748B' : visuals.textColor}
                        fontSize={stall.areaSqm >= 36 ? '7.5' : '5.5'}
                        fontWeight="medium"
                        className="pointer-events-none select-none opacity-85"
                      >
                        {stall.status === 'on_hold' ? 'HOLD' : stall.dimensions}
                      </text>
                    )}

                    {/* Booked / On Hold Visual Indicators */}
                    {stall.status === 'booked' && stall.w >= 28 && (
                      <g className="pointer-events-none">
                        <Lock className="w-2.5 h-2.5 text-slate-500" />
                      </g>
                    )}

                    {stall.status === 'on_hold' && (
                      <g className="pointer-events-none">
                        <circle
                          cx={stall.x + stall.w - 5}
                          cy={stall.y + 5}
                          r="4"
                          fill="#EA580C"
                        />
                        <path
                          d={`M ${stall.x + stall.w - 5} ${stall.y + 3} V ${stall.y + 5.2} L ${stall.x + stall.w - 3.6} ${stall.y + 6}`}
                          stroke="#FFFFFF"
                          strokeWidth="0.9"
                          strokeLinecap="round"
                          fill="none"
                        />
                      </g>
                    )}
                  </g>
                );
              })}

              {/* 8. FOOTER BANNER INSIDE CANVAS */}
              <g className="canvas-footer-banner">
                <rect x="36" y="885" width="652" height="32" rx="4" fill="#F8F9F6" stroke="#CBD5E1" strokeWidth="1" />
                <text x="362" y="902" textAnchor="middle" fill="#475569" fontSize="8" fontWeight="bold" letterSpacing="0.05em">
                  IIAB EXPO 2027 • 27 - 28 JULY 2027 • Pavilion 3 • Jio World Convention Centre (JWCC), BKC, Bandra East, Mumbai, INDIA
                </text>
              </g>
            </svg>
          </div>
        )}

        {/* Dynamic Floating Tooltip */}
        {hoveredStall && (
          <div className="absolute bottom-6 left-6 px-4 py-3 rounded-2xl bg-white/98 border border-slate-200 backdrop-blur-xl shadow-xl pointer-events-none flex items-center gap-5 text-xs animate-in fade-in duration-100 z-30">
            <div className="space-y-0.5">
              <div className="flex items-center gap-2">
                <span className="font-black text-slate-900 text-sm">Stall {hoveredStall.stall.id}</span>
                <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                  {hoveredStall.stall.category.replace('_', ' ')}
                </span>
              </div>
              <p className="text-slate-500">
                {hoveredStall.stall.dimensions} • {hoveredStall.stall.areaSqm} sqm
              </p>
            </div>

            <div className="border-l border-slate-200 pl-4 text-right">
              <span className="font-extrabold text-[#0A3D27] text-sm block">
                ₹{hoveredStall.stall.priceInr.toLocaleString('en-IN')}
              </span>
              <span
                className={`text-[11px] font-bold capitalize flex items-center justify-end gap-1 ${
                  hoveredStall.status === 'available'
                    ? 'text-[#0A3D27]'
                    : hoveredStall.status === 'on_hold'
                    ? 'text-amber-800'
                    : 'text-rose-700'
                }`}
              >
                {hoveredStall.status === 'available' && <CheckCircle2 className="w-3 h-3" />}
                {hoveredStall.status === 'on_hold' && <Clock className="w-3 h-3" />}
                {hoveredStall.status === 'booked' && <Lock className="w-3 h-3" />}
                {hoveredStall.status.replace('_', ' ')}
              </span>
            </div>
          </div>
        )}

        {/* Canvas Zoom Controls */}
        <div className="absolute bottom-6 right-6 flex items-center gap-1.5 p-1.5 rounded-2xl bg-white/95 border border-slate-200 backdrop-blur-md shadow-md z-20">
          <button
            onClick={() => handleZoom(0.15)}
            className="p-2 rounded-xl text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors cursor-pointer"
            title="Zoom In"
          >
            <ZoomIn className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleZoom(-0.15)}
            className="p-2 rounded-xl text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors cursor-pointer"
            title="Zoom Out"
          >
            <ZoomOut className="w-4 h-4" />
          </button>
          <button
            onClick={handleResetZoom}
            className="p-2 rounded-xl text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors cursor-pointer"
            title="Reset Canvas View"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default FloorPlan;
